// Mock data for startup analytics dashboard
// This structure is ready for Firebase integration

export interface MetricData {
  id: string;
  date: string;
  value: number;
  change?: number;
  changeType?: 'increase' | 'decrease';
}

export interface KPICard {
  id: string;
  title: string;
  value: string;
  change: number;
  changeType: 'increase' | 'decrease';
  icon: string;
  description: string;
}

export interface ChartData {
  name: string;
  value: number;
  date?: string;
  revenue?: number;
  users?: number;
  conversion?: number;
}

export interface InsightData {
  id: string;
  type: 'success' | 'warning' | 'info';
  title: string;
  description: string;
  action?: string;
  priority: 'high' | 'medium' | 'low';
}

// Simulated real-time data
export const generateRevenueData = (): ChartData[] => {
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return months.map((month, index) => ({
    name: month,
    revenue: Math.floor(Math.random() * 50000) + 20000 + (index * 2000),
    users: Math.floor(Math.random() * 1000) + 500 + (index * 50),
    conversion: Math.random() * 5 + 2,
    value: Math.floor(Math.random() * 50000) + 20000 + (index * 2000)
  }));
};

export const generateEngagementData = (): ChartData[] => {
  const days = Array.from({ length: 30 }, (_, i) => `Dia ${i + 1}`);
  return days.map((day, index) => ({
    name: day,
    value: Math.floor(Math.random() * 100) + 50 + Math.sin(index * 0.2) * 20,
    date: new Date(2024, 10, index + 1).toISOString().split('T')[0]
  }));
};

export const generateMarketingData = (): ChartData[] => {
  const channels = ['Google Ads', 'Facebook', 'Instagram', 'LinkedIn', 'Email', 'Orgânico'];
  return channels.map(channel => ({
    name: channel,
    value: Math.floor(Math.random() * 10000) + 1000,
    conversion: Math.random() * 8 + 1
  }));
};

export const mockKPIs: KPICard[] = [
  {
    id: '1',
    title: 'Receita Total',
    value: 'R$ 485.2K',
    change: 12.5,
    changeType: 'increase',
    icon: 'TrendingUp',
    description: 'Receita acumulada nos últimos 30 dias'
  },
  {
    id: '2',
    title: 'Usuários Ativos',
    value: '12.4K',
    change: 8.2,
    changeType: 'increase',
    icon: 'Users',
    description: 'Usuários ativos mensais'
  },
  {
    id: '3',
    title: 'Taxa de Conversão',
    value: '3.8%',
    change: -2.1,
    changeType: 'decrease',
    icon: 'Target',
    description: 'Conversão de visitantes para clientes'
  },
  {
    id: '4',
    title: 'CAC',
    value: 'R$ 125',
    change: -5.3,
    changeType: 'decrease',
    icon: 'DollarSign',
    description: 'Custo de Aquisição de Cliente'
  },
  {
    id: '5',
    title: 'LTV',
    value: 'R$ 2.1K',
    change: 15.7,
    changeType: 'increase',
    icon: 'Repeat',
    description: 'Lifetime Value do Cliente'
  },
  {
    id: '6',
    title: 'Churn Rate',
    value: '2.3%',
    change: 1.2,
    changeType: 'increase',
    icon: 'UserMinus',
    description: 'Taxa de cancelamento mensal'
  }
];

export const mockInsights: InsightData[] = [
  {
    id: '1',
    type: 'success',
    title: 'Crescimento Acelerado',
    description: 'Receita cresceu 25% nas últimas 4 semanas. Considere aumentar investimento em marketing.',
    action: 'Aumentar budget de marketing',
    priority: 'high'
  },
  {
    id: '2',
    type: 'warning',
    title: 'Taxa de Conversão em Queda',
    description: 'Conversão caiu 2.1% este mês. Revisar funil de vendas e experiência do usuário.',
    action: 'Otimizar funil de conversão',
    priority: 'high'
  },
  {
    id: '3',
    type: 'info',
    title: 'Oportunidade de Retenção',
    description: 'LTV/CAC ratio está em 16.8x, indicando boa saúde do negócio. Foque em retenção.',
    action: 'Implementar programa de fidelidade',
    priority: 'medium'
  },
  {
    id: '4',
    type: 'success',
    title: 'Canal de Alto Desempenho',
    description: 'Google Ads apresenta ROI 340% superior aos outros canais.',
    action: 'Realocar budget para Google Ads',
    priority: 'medium'
  }
];

// Simulated real-time updates
export const getRealtimeMetrics = () => {
  return {
    currentRevenue: Math.floor(Math.random() * 1000) + 15000,
    activeUsers: Math.floor(Math.random() * 100) + 450,
    conversionRate: (Math.random() * 2 + 3).toFixed(1),
    lastUpdated: new Date().toLocaleTimeString('pt-BR')
  };
};

// Firebase-ready data structure
export const firebaseDataStructure = {
  // Collection: startups/{startupId}/metrics
  metrics: {
    revenue: {
      daily: [] as MetricData[],
      monthly: [] as MetricData[],
      yearly: [] as MetricData[]
    },
    users: {
      active: [] as MetricData[],
      new: [] as MetricData[],
      retention: [] as MetricData[]
    },
    marketing: {
      channels: [] as ChartData[],
      campaigns: [] as MetricData[],
      costs: [] as MetricData[]
    }
  },
  // Collection: startups/{startupId}/insights
  insights: [] as InsightData[],
  // Collection: startups/{startupId}/kpis
  kpis: [] as KPICard[]
};

// Helper functions for Firebase integration
export const formatDataForFirebase = (data: any) => {
  return {
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

export const calculateGrowthRate = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

export const generateDateRange = (days: number): string[] => {
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
};